import { ApolloError } from '@apollo/client';

import type { ExperienceEvent } from '@atlassian/experience-tracker/ExperienceEvent';

interface GraphQLError {
	operation: string;
	message: string;
	path: string[];
	locations: unknown;
	extensions: unknown;
}

export const isApolloError = (error: unknown): boolean => error instanceof ApolloError;

export const hasGraphQlProperties = (error: unknown): error is GraphQLError => {
	if (typeof error !== 'object' || error === undefined || error === null) {
		return false;
	}

	const graphQlErrorKeys = ['operation', 'message', 'path', 'locations', 'extensions'];

	return graphQlErrorKeys.every((key) => key in error);
};

/**
 * Event metadata that are passed through the custom attribute.
 *
 * @param {ExperienceEvent} event - The experience event.
 * @param {string[]} keys - The keys to get the custom attributes for.
 * @returns {Record<string, unknown>} The custom attributes.
 */
export const getCustomAttributes = (
	event: ExperienceEvent,
	keys: string[],
): Record<string, unknown> =>
	keys.reduce<Record<string, unknown>>((acc, key) => {
		if (
			!event.attributes ||
			!('custom' in event.attributes) ||
			typeof event.attributes.custom !== 'object'
		) {
			return acc;
		}

		const value = event.attributes.custom?.[key];

		if (value === undefined) {
			return acc;
		}

		return { ...acc, [key]: value };
	}, {});
